import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Manejar el scroll automático cuando se carga la página con hash
  useEffect(() => {
    if (window.location.hash) {
      // Pequeño delay para asegurar que la página se haya cargado completamente
      setTimeout(() => {
        const element = document.querySelector(window.location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  // Función para manejar la navegación con scroll
  const handleScrollNavigation = (sectionId) => {
    const isHomePage = location.pathname === '/';
    
    if (isHomePage) {
      // Si estamos en la página de inicio, hacer scroll directo
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Si estamos en otra página, navegar a inicio con el hash
      navigate(`/${sectionId}`);
    }
    setIsOpen(false);
  };

  const navLinks = [
    { name: 'Inicio', to: '/', type: 'route' },
    { name: 'Cotizar viaje', to: '#quote', type: 'scroll' },
    { name: 'Galería', to: '#gallery', type: 'scroll' },
    { name: 'Servicios', to: '#services', type: 'scroll' },
    { name: 'Promociones', to: '#promotions', type: 'scroll' },
    { name: 'Testimonios', to: '#testimonials', type: 'scroll' },
    { name: 'Calculadora', to: '#calculator', type: 'scroll' },
    { name: 'FAQ', to: '#faq', type: 'scroll' },
    { name: 'Consejos', to: '/tips', type: 'route' },
    { name: 'Pagar', to: '/payment', type: 'route' },
    { name: 'Contacto', to: '#contact', type: 'scroll' }
  ];

  return (
    <nav className="fixed top-0 w-full bg-gradient-to-r from-pink-500 via-purple-600 to-blue-500 text-white z-50 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-3">
          <motion.div 
            className="flex items-center space-x-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <img 
              src="/images/Logo.PNG" 
              alt="Ratones Viajeros Logo" 
              className="h-8 sm:h-10 md:h-12 w-auto drop-shadow-lg"
            />
            <span className="text-xl sm:text-2xl md:text-2xl font-bold bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent drop-shadow-lg">
              Ratones Viajeros
            </span>
          </motion.div>

          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            {navLinks.slice(0, 6).map((link, index) => (  
              <motion.div key={link.name}>
                {link.type === 'scroll' ? (
                  <button
                    onClick={() => handleScrollNavigation(link.to)}
                    className="text-white/90 hover:text-yellow-300 font-medium transition-all duration-300 relative group px-2 sm:px-3 py-2 rounded-lg text-sm sm:text-base cursor-pointer"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {link.name}
                    <span className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-300 group-hover:w-full transition-all duration-300"></span>
                  </button>
                ) : (
                  <Link
                    to={link.to}
                    className="text-white/90 hover:text-yellow-300 font-medium transition-all duration-300 relative group px-2 sm:px-3 py-2 rounded-lg text-sm sm:text-base"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                    <span className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-300 group-hover:w-full transition-all duration-300"></span>
                  </Link>
                )}
              </motion.div>
            ))}
            <motion.div
              className="ml-2 sm:ml-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Link
                to="/payment"
                className="bg-yellow-300 text-purple-900 px-4 sm:px-6 py-2 sm:py-2 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Pagar Ahora
              </Link>
            </motion.div>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white hover:text-yellow-300 p-2"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden pb-4 space-y-2 bg-white/10 backdrop-blur-md rounded-b-2xl"
          >
            {navLinks.map((link, index) => (
              <div key={link.name}>
                {link.type === 'scroll' ? (
                  <button
                    onClick={() => handleScrollNavigation(link.to)}
                    className="block w-full text-left text-white/95 hover:text-yellow-300 font-medium transition-colors duration-300 py-3 px-4 text-sm"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className="block text-white/95 hover:text-yellow-300 font-medium transition-colors duration-300 py-3 px-4 text-sm"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;